import { splitBowlerName } from "./bowlerName";
import { givenNameAliases } from "./bowlerNicknames";
import {
  lastFirstKey,
  nameKey,
  normalizeMatchName,
  normalizeUsbcId,
  uniqueIndex,
} from "./bowlerMatchKeys";

export const normalizeEmail = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

export const normalizePhone = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits.length === 10 ? digits : "";
};

export const damerauLevenshtein = (left, right) => {
  const a = String(left || "");
  const b = String(right || "");
  const m = a.length;
  const n = b.length;
  if (a === b) return 0;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i += 1) dp[i][0] = i;
  for (let j = 0; j <= n; j += 1) dp[0][j] = j;
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        dp[i][j] = Math.min(dp[i][j], dp[i - 2][j - 2] + cost);
      }
    }
  }
  return dp[m][n];
};

const OCR_SWAPS = [
  [/0/g, "o"],
  [/o/g, "0"],
  [/1/g, "l"],
  [/l/g, "1"],
  [/1/g, "i"],
  [/i/g, "1"],
  [/rn/g, "m"],
  [/m/g, "rn"],
];

export const ocrNameVariants = (name) => {
  const base = normalizeMatchName(name);
  if (!base) return [];
  const variants = new Set([base]);
  OCR_SWAPS.forEach(([pattern, replacement]) => {
    const next = base.replace(pattern, replacement);
    if (next && next !== base) variants.add(next);
  });
  variants.delete(base);
  return [...variants];
};

export const givenNameKeys = (firstName) => {
  const norm = normalizeMatchName(firstName);
  if (!norm) return [];
  const keys = new Set([norm]);
  const firstToken = norm.split(" ")[0];
  keys.add(firstToken);
  givenNameAliases(firstToken).forEach((alias) => keys.add(alias));
  return [...keys];
};

const distinctPeople = (list, idOf) => {
  const seen = new Set();
  const people = [];
  (list || []).forEach((person) => {
    if (!person) return;
    const id = idOf(person);
    if (!id || seen.has(id)) return;
    seen.add(id);
    people.push(person);
  });
  return people;
};

const lastNameOf = (name) =>
  normalizeMatchName(splitBowlerName(name).lastName);

const firstNameOf = (name) =>
  normalizeMatchName(splitBowlerName(name).firstName);

const averageDistance = (written, personAverage) => {
  if (written == null || personAverage == null) return Number.POSITIVE_INFINITY;
  return Math.abs(Number(written) - Number(personAverage));
};

/**
 * Rank people against a typed or OCR name. Auto only on unique keys.
 * Fuzzy and average are never auto.
 */
export const searchPeople = ({
  query,
  usbcID,
  email,
  phone,
  writtenAverage,
  people = [],
  idOf = (person) => person.id,
  nameOf = (person) => person.name,
  usbcOf = (person) => person.usbcId || person.usbcID,
  emailOf = (person) => person.email,
  phoneOf = (person) => person.phone,
  averageOf = () => null,
  excludeIds = [],
  limit = 8,
}) => {
  const pool = (people || []).filter(
    (person) => !excludeIds.includes(idOf(person))
  );
  const usbc = normalizeUsbcId(usbcID);
  const mail = normalizeEmail(email);
  const tel = normalizePhone(phone);
  const trimmed = String(query || "").trim();
  const full = nameKey(trimmed);
  const lastFirst = lastFirstKey(trimmed);
  const queryLast = lastNameOf(trimmed);
  const queryFirst = firstNameOf(trimmed);
  const queryNorm = normalizeMatchName(trimmed);
  const written =
    writtenAverage == null || writtenAverage === ""
      ? null
      : Number(writtenAverage);
  const writtenNum = Number.isFinite(written) ? written : null;

  const byUsbc = uniqueIndex(pool, (person) => normalizeUsbcId(usbcOf(person)));
  const byEmail = uniqueIndex(pool, (person) =>
    normalizeEmail(emailOf(person))
  );
  const byPhone = uniqueIndex(pool, (person) =>
    normalizePhone(phoneOf(person))
  );
  const byFullName = uniqueIndex(pool, (person) => nameKey(nameOf(person)));
  const byLastFirst = uniqueIndex(pool, (person) =>
    lastFirstKey(nameOf(person))
  );
  const byLastName = uniqueIndex(pool, (person) => lastNameOf(nameOf(person)));

  const seen = new Set();
  const results = [];
  let lockedAuto = false;
  const add = (person, status, reason) => {
    if (!person) return;
    const id = idOf(person);
    if (!id || seen.has(id)) return;
    let nextStatus = status;
    if (status === "autoMatched" && lockedAuto) {
      nextStatus = "possibleMatch";
    }
    if (nextStatus === "autoMatched") lockedAuto = true;
    seen.add(id);
    results.push({
      id,
      person,
      status: nextStatus,
      reason,
      average: averageOf(person),
    });
  };

  if (usbc && byUsbc.unique.has(usbc)) {
    add(byUsbc.unique.get(usbc), "autoMatched", "usbc");
  } else if (usbc) {
    (byUsbc.buckets.get(usbc) || []).forEach((person) =>
      add(person, "possibleMatch", "multipleUsbc")
    );
  }

  if (mail && byEmail.unique.has(mail)) {
    add(byEmail.unique.get(mail), "autoMatched", "email");
  } else if (mail) {
    (byEmail.buckets.get(mail) || []).forEach((person) =>
      add(person, "possibleMatch", "multipleEmail")
    );
  }

  if (tel && byPhone.unique.has(tel)) {
    add(byPhone.unique.get(tel), "autoMatched", "phone");
  } else if (tel) {
    (byPhone.buckets.get(tel) || []).forEach((person) =>
      add(person, "possibleMatch", "multiplePhone")
    );
  }

  if (full && byFullName.unique.has(full)) {
    add(byFullName.unique.get(full), "autoMatched", "exactName");
  } else if (full) {
    (byFullName.buckets.get(full) || []).forEach((person) =>
      add(person, "possibleMatch", "multipleNames")
    );
  }

  if (lastFirst && byLastFirst.unique.has(lastFirst)) {
    add(byLastFirst.unique.get(lastFirst), "autoMatched", "lastFirst");
  } else if (lastFirst) {
    (byLastFirst.buckets.get(lastFirst) || []).forEach((person) =>
      add(person, "possibleMatch", "multipleNames")
    );
  }

  if (queryLast && queryFirst) {
    const nicknameHits = [];
    givenNameKeys(queryFirst).forEach((given) => {
      const key = `${queryLast}\t${given}`;
      if (byLastFirst.unique.has(key)) {
        nicknameHits.push(byLastFirst.unique.get(key));
      } else {
        nicknameHits.push(...(byLastFirst.buckets.get(key) || []));
      }
    });
    const nicknamePeople = distinctPeople(nicknameHits, idOf);
    if (nicknamePeople.length === 1) {
      add(nicknamePeople[0], "autoMatched", "nickname");
    } else {
      nicknamePeople.forEach((person) =>
        add(person, "possibleMatch", "nickname")
      );
    }
  }

  if (queryLast && queryFirst) {
    const lastPeople = byLastName.buckets.get(queryLast) || [];
    const lastUnique = byLastName.unique.has(queryLast);
    const initialHits = lastPeople.filter((person) => {
      const first = firstNameOf(nameOf(person));
      return Boolean(first) && first.startsWith(queryFirst);
    });
    if (lastUnique && initialHits.length === 1) {
      add(initialHits[0], "autoMatched", "firstInitial");
    } else {
      initialHits.forEach((person) =>
        add(person, "possibleMatch", "firstInitial")
      );
    }
  }

  if (queryLast && queryFirst) {
    const ocrHits = [];
    ocrNameVariants(trimmed).forEach((variant) => {
      const variantFull = nameKey(variant);
      const variantLastFirst = lastFirstKey(variant);
      if (variantFull && byFullName.unique.has(variantFull)) {
        ocrHits.push(byFullName.unique.get(variantFull));
      } else if (variantFull) {
        ocrHits.push(...(byFullName.buckets.get(variantFull) || []));
      }
      if (variantLastFirst && byLastFirst.unique.has(variantLastFirst)) {
        ocrHits.push(byLastFirst.unique.get(variantLastFirst));
      } else if (variantLastFirst) {
        ocrHits.push(...(byLastFirst.buckets.get(variantLastFirst) || []));
      }
    });
    const ocrPeople = distinctPeople(ocrHits, idOf);
    if (ocrPeople.length === 1) {
      add(ocrPeople[0], "autoMatched", "ocrSwap");
    } else {
      ocrPeople.forEach((person) => add(person, "possibleMatch", "ocrSwap"));
    }
  }

  if (queryLast && queryFirst) {
    pool.forEach((person) => {
      const personLast = lastNameOf(nameOf(person));
      const personFirst = firstNameOf(nameOf(person));
      if (!personLast || !personFirst) return;
      const lastExact = personLast === queryLast;
      const firstExact = personFirst === queryFirst;
      const firstNear =
        lastExact &&
        !firstExact &&
        damerauLevenshtein(personFirst, queryFirst) <= 1;
      const lastNear =
        firstExact &&
        !lastExact &&
        damerauLevenshtein(personLast, queryLast) <= 1;
      if (firstNear || lastNear) {
        add(person, "possibleMatch", "fuzzy");
      }
    });
  }

  if (queryNorm.length >= 2) {
    pool.forEach((person) => {
      if (results.length >= limit * 3) return;
      const personNorm = nameKey(nameOf(person));
      if (personNorm.includes(queryNorm)) {
        add(person, "searchHit", "nameContains");
      }
    });
  }

  const rank = (row) => {
    if (row.status === "autoMatched") return 0;
    if (row.status === "possibleMatch") return 1;
    return 2;
  };

  results.sort((left, right) => {
    const byStatus = rank(left) - rank(right);
    if (byStatus !== 0) return byStatus;
    if (left.status !== "possibleMatch" || writtenNum == null) return 0;
    return (
      averageDistance(writtenNum, left.average) -
      averageDistance(writtenNum, right.average)
    );
  });

  return results.slice(0, limit);
};

export const uniqueAutoMatch = (results) => {
  const auto = (results || []).filter((row) => row.status === "autoMatched");
  return auto.length === 1 ? auto[0] : null;
};

export const needsOrgBowlerPick = (bowler) =>
  Boolean(String(bowler?.name || "").trim()) &&
  !bowler?.existingBowler &&
  Array.isArray(bowler?.lookupResults) &&
  bowler.lookupResults.length > 0;

export const searchOrgBowlersForTeamCreate = ({
  query,
  usbcID,
  email,
  phone,
  writtenAverage,
  orgBowlers,
  excludeIds = [],
  limit = 10,
}) =>
  searchPeople({
    query,
    usbcID,
    email,
    phone,
    writtenAverage,
    people: orgBowlers,
    usbcOf: (bowler) => bowler.usbcID || bowler.usbcId,
    averageOf: (bowler) =>
      bowler.average == null || bowler.average === ""
        ? null
        : Number(bowler.average),
    excludeIds,
    limit,
  }).map((row) => ({
    ...row.person,
    matchStatus: row.status,
    matchReason: row.reason,
  }));

export const matchReasonLabel = (reason) => {
  switch (reason) {
    case "usbc":
    case "multipleUsbc":
      return "Same USBC ID";
    case "exactName":
    case "multipleNames":
    case "lastFirst":
      return "Same name";
    case "email":
    case "multipleEmail":
      return "Same email";
    case "phone":
    case "multiplePhone":
      return "Same phone";
    case "nickname":
    case "firstInitial":
    case "ocrSwap":
    case "fuzzy":
      return "Similar name";
    case "nameContains":
      return "Name matches";
    default:
      return "";
  }
};

export const isObviousBowlerMatch = (bowler, query) => {
  if (!bowler) return false;
  if (
    bowler.matchStatus === "autoMatched" ||
    bowler.matchStatus === "possibleMatch"
  ) {
    return true;
  }
  const typed = normalizeMatchName(query);
  const name = normalizeMatchName(bowler.name);
  return (
    typed.length >= 4 &&
    name.startsWith(typed) &&
    (bowler.matchReason === "nameContains" || !bowler.matchReason)
  );
};
